import React, {useState} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {makeStyles} from '@mui/styles';
import {theme} from '../../../theme/theme';
import {
  Property,
  useGetPropertyByPropertyIdInvoicesQuery,
} from '../../../redux/residentPortalApi';
import LeviesSection from "../LeviesSection";
import MetersSection from "../MetersSection";
import WifiSection from '../internet/WifiSection';
import PaymentHistory from "../PaymentHistory";
import LeviesStatement from '../LeviesStatement';
import SolarRentalSection from '../SolarRentalSection';

interface OwnProps {
  property: Property;
}

const useStyles = makeStyles({
  container: {
    padding: theme.spacing(3),
  },
});

const UtilitiesTabContent: React.FC<OwnProps> = ({property}) => {
  const classes = useStyles();
  const [expandedSection, setExpandedSection] = useState<string>('levies');

  const {data: propertyInvoices} = useGetPropertyByPropertyIdInvoicesQuery(
    {propertyId: property?.id ?? ''},
    {skip: !property?.id}
  );
  const invoices = propertyInvoices?.filter((invoice) =>
    invoice.invoiceNumber.toUpperCase().startsWith('LEV-')
  );

  const outstandingLeviesBalance =
    invoices?.filter((invoice) => invoice.status === 'unpaid')?.reduce((acc, invoice) => {
      return acc + (invoice.invoice?.totalCost || 0);
    }, 0) || 0;

  const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : '');
  };

  return (
    <Box className={classes.container}>
      {(Array.isArray(property?.meters) && property?.meters.length > 0) && (
        <Accordion
          disableGutters
          expanded={expandedSection === 'meters'}
          onChange={handleAccordionChange('meters')}
          sx={{ mb: 2, borderRadius: 2, border: '1px solid #e5e7eb', '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 600 }}>Meters</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <MetersSection property={property} outstandingLeviesBalance={outstandingLeviesBalance}/>
          </AccordionDetails>
        </Accordion>
      )}

      {property.internetCapable && (
        <Accordion
          disableGutters
          expanded={expandedSection === 'internet'}
          onChange={handleAccordionChange('internet')}
          sx={{ mb: 2, borderRadius: 2, border: '1px solid #e5e7eb', '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 600 }}>Internet</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <WifiSection property={property}/>
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion
        disableGutters
        expanded={expandedSection === 'levies'}
        onChange={handleAccordionChange('levies')}
        sx={{ mb: 2, borderRadius: 2, border: '1px solid #e5e7eb', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>Levies Invoices</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <LeviesSection property={property} outstandingLeviesBalance={outstandingLeviesBalance} invoices={invoices}/>
        </AccordionDetails>
      </Accordion>

      <Accordion
        disableGutters
        expanded={expandedSection === 'statement'}
        onChange={handleAccordionChange('statement')}
        sx={{ mb: 2, borderRadius: 2, border: '1px solid #e5e7eb', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>Levies Statement</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <LeviesStatement propertyId={property?.id ?? ""} invoices={invoices} />
        </AccordionDetails>
      </Accordion>

      <Accordion
        disableGutters
        expanded={expandedSection === 'solar-rental'}
        onChange={handleAccordionChange('solar-rental')}
        sx={{ mb: 2, borderRadius: 2, border: '1px solid #e5e7eb', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>Solar Rental</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <SolarRentalSection
            propertyLabel={`${property?.development || ''}${property?.phase ? ` - ${property.phase}` : ''}${property?.unitNumber ? ` Unit ${property.unitNumber}` : ''}`.trim()}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion
        disableGutters
        expanded={expandedSection === 'payments'}
        onChange={handleAccordionChange('payments')}
        sx={{ mb: 2, borderRadius: 2, border: '1px solid #e5e7eb', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>Payments History</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <PaymentHistory propertyId={property?.id ?? ""} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default UtilitiesTabContent;
